import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, Trash2 } from 'lucide-react';
import { adminDossiersApi } from '@/api';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { formatDate, statusLabel } from '@/lib/utils';

const FILTER_TABS = ['tous', 'brouillon', 'en_cours', 'termine', 'genere', 'envoye'];
const FILTER_TAB_LABELS = {
  tous: 'Tous',
  brouillon: 'Brouillon',
  en_cours: 'En cours',
  termine: 'Terminé',
  genere: 'Généré',
  envoye: 'Envoyé',
};

export default function DossiersAdminPage() {
  const [dossiers, setDossiers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statut, setStatut] = useState('tous');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchDossiers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { skip, limit, sort_by: sortBy, sort_order: sortOrder };
      if (searchQuery) params.search = searchQuery;
      if (statut !== 'tous') params.statut = statut;
      const data = await adminDossiersApi.list(params);
      setDossiers(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, sortBy, sortOrder, searchQuery, statut]);

  useEffect(() => {
    fetchDossiers();
  }, [fetchDossiers]);

  const handleDelete = async (dossier) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le dossier ${dossier.reference} ?\n\n` +
          'Cette action est irréversible et supprimera toutes les données associées ' +
          '(expertises, parcelles, biens, photos, valorisations...).'
      )
    ) {
      return;
    }
    try {
      await adminDossiersApi.delete(dossier.id);
      fetchDossiers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setSkip(0);
  };

  const handleTabChange = (tab) => {
    setStatut(tab);
    setSkip(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setSkip(0);
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Gestion des dossiers</h1>
          <p className="text-slate-600 mt-2">
            Visualisez et gérez tous les dossiers d&apos;expertise
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par référence ou nom client..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                statut === tab
                  ? 'border-carex-600 text-carex-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {FILTER_TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && <LoadingSpinner message="Chargement des dossiers..." />}

        {/* Error */}
        {error && <ErrorMessage message={error} onRetry={fetchDossiers} />}

        {/* Empty */}
        {!loading && !error && dossiers.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
            <p className="text-slate-500 text-lg">
              {searchQuery
                ? 'Aucun dossier ne correspond à votre recherche.'
                : 'Aucun dossier enregistré.'}
            </p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && dossiers.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th
                    className="text-left px-6 py-3 font-semibold text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('reference')}
                  >
                    Référence{sortIndicator('reference')}
                  </th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Client</th>
                  <th className="text-left px-6 py-3 font-semibold text-slate-700">Expert</th>
                  <th
                    className="text-left px-6 py-3 font-semibold text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('statut')}
                  >
                    Statut{sortIndicator('statut')}
                  </th>
                  <th
                    className="text-left px-6 py-3 font-semibold text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('created_at')}
                  >
                    Date mandat{sortIndicator('created_at')}
                  </th>
                  <th className="text-center px-6 py-3 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {dossiers.map((dossier) => (
                  <tr key={dossier.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-900">{dossier.reference}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{dossier.client_nom || '—'}</td>
                    <td className="px-6 py-4 text-slate-600">{dossier.expert_nom || '—'}</td>
                    <td className="px-6 py-4">
                      <Badge variant={dossier.statut}>{statusLabel(dossier.statut)}</Badge>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(dossier.date_mandat)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/dossiers/${dossier.id}`}
                          className="p-2 text-carex-600 hover:bg-carex-50 rounded-lg transition"
                          title="Voir le dossier"
                        >
                          <Eye size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(dossier)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination + Summary */}
        {!loading && !error && dossiers.length > 0 && (
          <>
            <Pagination
              total={total}
              skip={skip}
              limit={limit}
              onChange={(newSkip) => setSkip(newSkip)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setSkip(0);
              }}
            />
            <div className="mt-4 text-sm text-slate-600">
              {dossiers.length} dossier{dossiers.length !== 1 ? 's' : ''} affiché
              {dossiers.length !== 1 ? 's' : ''}
              {searchQuery && ` (filtré par "${searchQuery}")`}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
